/**
 * A stable identifier for this browser, sent with every login so the backend
 * can keep ONE session row per device instead of adding a new one each time
 * the user signs in (see Manage Sessions).
 *
 * Generated once and kept in localStorage. It must therefore survive logout —
 * AuthService.clearStorageKeepRemember() preserves this key explicitly; drop it
 * from that list and duplicate sessions come straight back.
 *
 * It identifies a browser profile, not a person: it is written before anyone
 * is authenticated, holds no user data, and is scoped to this origin.
 */
export const DEVICE_ID_KEY = 'device_id';

/** Reads this browser's device id, creating and storing one on first use. */
export function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) {
      return existing;
    }

    const deviceId = newDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
    return deviceId;
  } catch {
    // Private mode / storage disabled: return an ephemeral id rather than
    // failing the login. The server falls back to its user-agent fingerprint
    // for anything it cannot match, so sessions still de-duplicate.
    return '';
  }
}

function newDeviceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Older browsers (and non-secure contexts, where randomUUID is unavailable).
  return `dev_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}
